//Cache is a Weakmap so we don't create unnecessary duplicates of the readonlyView
const cache = new WeakMap();

const disabledMethod = new Set([
  'copyWithin',
  'fill',
  'pop',
  'push',
  'reverse',
  'shift',
  'sort',
  'splice',
  'unshift',
]);

const digitsRegex = /\d+/;

type ItemProjection<T, U> = (item: T) => U;

//getWithProjectTrapFactory(): Create a get trap that maps item to another type
//itemProjection: Projects an item of type T to type U `(item: T) => U`
const getWithProjectTrapFactory =
  <T, U>(itemProjection: ItemProjection<T, U>) =>
  (
    target: T[],
    p: keyof typeof target,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    receiver: any,
  ): U | T => {
    if (disabledMethod.has(p as string)) {
      throw new Error(
        `Cannot call '.${p as string}()' on a readonly view of an array`,
      );
    }
    const v = Reflect.get(target, p, receiver);
    digitsRegex.lastIndex = 0;
    return typeof p === 'string' && digitsRegex.test(p) ? itemProjection(v) : v;
  };

const getTrap = <T>(
  target: T[],
  p: keyof typeof target,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  receiver: any,
) => {
  if (disabledMethod.has(p as string)) {
    throw new Error(
      `Cannot call '.${p as string}()' on a readonly view of an array`,
    );
  }
  return Reflect.get(target, p, receiver);
};

const setTrap = () => {
  throw new Error('This is a readonly view of an array');
};

//This provides a simple proxy view to an array that cannot be mutated
//* The optional `itemProjection` is useful when a readonly array should be the projection of another array
//  * If itemProjection is provided, then the readonly projected view is more efficient when only some indices
//    will be accessed.  Without `itemProjection`, then the whole projected array must be calculated with something
//    like `createReadonlyArrayView(someArray.map(itemProjection))` which is unnecessary if only some of the projected
//    indices are accessed.
//* Note: readonlyView.forEach((_, i, _array) => { console.log(_, i, _array === readonlyView); })
//  shows that _array passed to the callback is the readonlyArrayView and not the original array.
//  This is similarly true for .every(), .some(), map(), .filter(), .reduce(), & .reduceRight().
export const createReadonlyArrayView = <T, U = T>(
  items: T[],
  itemProjection?: ItemProjection<T, U>,
): ReadonlyArray<
  typeof itemProjection extends ItemProjection<T, U> ? U : T
> => {
  if (cache.has(items) && typeof itemProjection !== 'function') {
    return cache.get(items);
  }
  const readonlyView = new Proxy(items, {
    get: (typeof itemProjection === 'function'
      ? getWithProjectTrapFactory(itemProjection)
      : getTrap) as ProxyHandler<typeof items>['get'],
    set: setTrap,
  }) as unknown as ReadonlyArray<
    typeof itemProjection extends ItemProjection<T, U> ? U : T
  >;
  if (typeof itemProjection !== 'function') {
    cache.set(items, readonlyView);
  }
  return readonlyView;
};
